import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface BlackforestlabsFlux2ProParams {
  /**
   * 图像生成的文本提示词
   * @example "一张宁静的森林小溪照片，阳光透过树叶照射下来"
   */
  prompt: string;
  /**
   * 是否启用提示词优化增强
   * @default true
   */
  prompt_upsampling?: boolean;
  /**
   * 输出图像宽度（像素），0表示自动
   * @default 0
   * @range 64 - ∞
   */
  width?: number;
  /**
   * 输出图像宽度（像素），0表示自动
   * @default 0
   * @range 64 - ∞
   */
  height?: number;
  /**
   * 内容安全容忍度（0-5），0最严格，5最宽松
   * @default 2
   * @range 0 - 5
   */
  safety_tolerance?: number;
  /**
   * 输出图像格式
   * @default "jpeg"
   * @values jpeg | png
   */
  output_format?: 'jpeg' | 'png';
  /**
   * 随机种子，用于结果可复现
   * @example 42
   */
  seed?: number;
  /**
   * 接收Webhook通知的URL
   */
  webhook_url?: string;
  /**
   * Webhook签名验证密钥
   */
  webhook_secret?: string;
}

export interface BlackforestlabsFlux2ProResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * blackforestlabs_flux_2_pro
 *
 * <p>使用FLUX.2 [PRO]模型生成或编辑图像，支持最多8张参考图像的多参考编辑</p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, blackforestlabsFlux2Pro } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await blackforestlabsFlux2Pro({
 *   // 参数见 BlackforestlabsFlux2ProParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 BlackforestlabsFlux2ProParams 接口定义
 * @returns 生成的资源列表
 */
export async function blackforestlabsFlux2Pro(
  params: BlackforestlabsFlux2ProParams,
): Promise<BlackforestlabsFlux2ProResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'blackforestlabs_flux_2_pro',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: BlackforestlabsFlux2ProResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
